/**
 * API de Caja / pagos.
 */
import { apiFetch } from "./client";

export type MetodoPago = "EFECTIVO" | "YAPE" | "PLIN" | "TRANSFERENCIA" | "TARJETA";
export type EstadoPago = "PAGADO" | "PENDIENTE_VALIDACION" | "RECHAZADO";

export interface Pago {
  id: number;
  cita_id: number;
  monto: string;
  metodo: MetodoPago;
  /** MOSTRADOR o APP: de dónde vino el pago. Ver migración 0054. */
  origen?: string;
  estado: EstadoPago;
  tiene_constancia: boolean;
  nota: string | null;
  /** Lo que escribió caja al rechazar. */
  motivo_rechazo?: string | null;
  created_at: string;
  /** Contexto de la cita; null si el backend devolvió el pago recién creado. */
  paciente_nombre: string | null;
  profesional_nombre: string | null;
  especialidad_nombre: string | null;
  cita_inicio: string | null;
}

export interface PagoCreate {
  cita_id: number;
  metodo: MetodoPago;
  /** MOSTRADOR o APP: de dónde vino el pago. Ver migración 0054. */
  origen?: string;
  monto?: string | number | null;
  estado?: "PAGADO" | "PENDIENTE_VALIDACION";
  nota?: string | null;
}

export function listPagos(estado?: EstadoPago): Promise<Pago[]> {
  const q = estado ? `?estado=${estado}` : "";
  return apiFetch<Pago[]>(`/v1/pagos${q}`);
}

export function registrarPago(payload: PagoCreate): Promise<Pago> {
  return apiFetch<Pago>("/v1/pagos", { method: "POST", body: payload });
}

/**
 * Aprueba o rechaza una constancia.
 *
 * El motivo solo cuenta al rechazar, y es lo que el paciente verá en su app:
 * sin él, su pantalla volvía a "Pasa por caja" como si nunca hubiera subido
 * nada y acababa mandando la misma captura otra vez.
 */
export function validarPago(
  pagoId: number,
  aprobar: boolean,
  motivo?: string,
): Promise<Pago> {
  const m = motivo?.trim() ? `&motivo=${encodeURIComponent(motivo.trim())}` : "";
  return apiFetch<Pago>(`/v1/pagos/${pagoId}/validar?aprobar=${aprobar}${m}`, {
    method: "POST",
  });
}

/** Sube la constancia (imagen/PDF) de un pago. apiFetch fuerza JSON, así que usamos fetch crudo. */
export async function uploadConstancia(pagoId: number, file: File): Promise<Pago> {
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const formData = new FormData();
  formData.append("file", file);
  const base = (import.meta.env.VITE_API_BASE_URL ?? "/api") as string;
  const response = await fetch(`${base}/v1/pagos/${pagoId}/constancia`, {
    method: "POST",
    headers: tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {},
    body: formData,
  });
  if (!response.ok) throw new Error("No se pudo subir la constancia.");
  return response.json();
}

/**
 * Descarga la constancia como blob para mostrarla en pantalla.
 *
 * No sirve un <img src="…"> a pelo: el endpoint exige el token en la cabecera
 * y el navegador no lo manda en la carga de una imagen. Además es un
 * comprobante de pago —dato sensible—, así que no puede quedar en una URL
 * pública que cualquiera abra sin sesión.
 *
 * Quien lo llame debe hacer URL.revokeObjectURL cuando termine.
 */
export async function fetchConstancia(
  pagoId: number,
): Promise<{ url: string; tipo: string }> {
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const base = (import.meta.env.VITE_API_BASE_URL ?? "/api") as string;
  const response = await fetch(`${base}/v1/pagos/${pagoId}/constancia`, {
    headers: tokens?.access_token
      ? { Authorization: `Bearer ${tokens.access_token}` }
      : {},
  });
  if (!response.ok) throw new Error("No se pudo cargar la constancia.");
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), tipo: blob.type };
}
